import type {
  AnalyticsRange,
  StaffAnalyticsMetric,
  StaffAnalyticsSelection,
} from "@lafayette/schemas";
import { useEffect } from "react";

import { AsyncState } from "../../ui/async-state";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Notice } from "../../ui/notice";
import { RefusedState } from "../components/RefusedState";
import { type CustomerView, isRefusal } from "../queries";
import { ExploreChart } from "./ExploreChart";
import { ExploreNavigator } from "./ExploreNavigator";
import { ExploreToolbar } from "./ExploreToolbar";
import { type ExploreNavigation, useExploreAddress } from "./explore-address";
import { useExploreMetric, useExploreSelection, useExploreVisits } from "./explore-queries";
import { METRIC_INFO } from "./metric-catalog";
import { SelectionPanel } from "./SelectionPanel";
import { VisitsTable } from "./VisitsTable";
import type { Zoom } from "./zoom-math";

// The Explore tab (Paper "Analytics · Zoom in"): the chart of one metric, the navigator under it, "In this selection" and "Visits in this range".
// Every control is in the address (`explore-address.ts`). Esc goes back one level of zoom.

/** Esc goes back one level while the tab is open. It leaves a field, a select and a dialog alone: they have their own Esc. */
function useEscapeToGoUp(goUp: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const { target } = event;
      if (
        target instanceof Element &&
        target.closest("input, select, textarea, [role='dialog'], [role='listbox']") !== null
      ) {
        return;
      }
      goUp();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [goUp]);
}

function AppDataNotice({ metric, name }: Readonly<{ metric: StaffAnalyticsMetric; name: string }>) {
  if (!METRIC_INFO[metric.metric].app || metric.availability === "AVAILABLE") return null;
  const words = {
    OPTED_OUT: `${name} chose not to share app analytics, so this number is not available. Pick a number from the records, such as Requests made.`,
    ANALYTICS_OFF:
      "App activity is not recorded yet, so this number is not available. Pick a number from the records.",
    VENDOR_UNAVAILABLE:
      "App activity could not be read right now. Pick a number from the records or try again later.",
  } as const;
  return (
    <Notice tone={metric.availability === "OPTED_OUT" ? "info" : "neutral"} role="note">
      {words[metric.availability]}
    </Notice>
  );
}

function Loading({ title }: Readonly<{ title: string }>) {
  return <AsyncState kind="loading" title={title} message="One moment." />;
}

type TabProps = Readonly<{
  customerId: string;
  range: AnalyticsRange;
  view: CustomerView;
  tz: string;
  overviewHref: string;
  /** The Events tab address, with the given extra parameters (a visit, a window). */
  eventsHref: (extra: Readonly<Record<string, string>>) => string;
}>;

type Loaded = Readonly<{
  metric: StaffAnalyticsMetric;
  whole: StaffAnalyticsMetric;
}>;

function ChartCard({
  loaded,
  nav,
  tz,
}: Readonly<{ loaded: Loaded; nav: ExploreNavigation; tz: string }>) {
  const { metric, whole } = loaded;
  const info = METRIC_INFO[metric.metric];
  const bound: Zoom = { from: metric.access.window.from, to: metric.access.window.to };
  return (
    <Card className="min-w-0 rounded-2xl">
      <section aria-labelledby="explore-chart-title">
        <CardHeader className="pb-3">
          <CardTitle as="h2" id="explore-chart-title" className="text-xl leading-6">
            {info.title(metric.interval)}
          </CardTitle>
          <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
            {info.app ? "Counted by the start of each visit. " : null}
            {previousNote(metric)}
          </p>
        </CardHeader>
        <CardContent>
          {metric.series.length === 0 ? (
            <p className="m-0 text-sm text-muted-foreground">
              There is no number to draw for this selection.
            </p>
          ) : (
            <ExploreChart
              metric={metric}
              interval={metric.interval}
              tz={tz}
              bound={bound}
              onZoom={(zoom) => nav.zoomTo(zoom)}
            />
          )}
          <ExploreNavigator
            metric={whole}
            zoom={nav.zoom}
            tz={tz}
            onZoom={(zoom, mode) => nav.zoomTo(zoom, mode)}
            onClear={nav.clearZoom}
          />
        </CardContent>
      </section>
    </Card>
  );
}

/** Why there is no previous period, in words, when the answer says so. */
function previousNote(metric: StaffAnalyticsMetric): string | null {
  if (metric.previousUnavailable === "RETENTION")
    return "The period before is older than 13 months. ";
  if (metric.previousUnavailable === "NO_PERIOD")
    return "There is no period before the whole history. ";
  return null;
}

/** The three reads of the tab beside the chart, and the one that draws the navigator. */
function useExploreReads(
  view: CustomerView,
  customerId: string,
  range: AnalyticsRange,
  nav: ExploreNavigation,
) {
  const base = { customerId, range, zoom: nav.zoom, enabled: true } as const;
  const main = useExploreMetric(view, {
    ...base,
    metric: nav.metric,
    interval: nav.interval,
    split: nav.split,
    compare: nav.compare,
  });
  const whole = useExploreMetric(
    view,
    {
      customerId,
      range,
      zoom: null,
      metric: nav.metric,
      interval: null,
      split: "none",
      compare: false,
      enabled: true,
    },
    "whole",
  );
  const selection = useExploreSelection(view, { ...base, compare: nav.compare });
  const visits = useExploreVisits(view, base);
  return { main, whole, selection, visits };
}

/** The numbers could not be read. A zoom may be the cause (it is outside the range), so the retry clears it first. */
function ExploreFailure({ nav, retry }: Readonly<{ nav: ExploreNavigation; retry: () => void }>) {
  const zoomed = nav.zoom !== null;
  return (
    <AsyncState
      kind="error"
      title="The numbers could not be loaded"
      message={
        zoomed
          ? "The selection may be outside the range. Clear it or try again."
          : "Nothing was changed. Try again in a moment."
      }
      onRetry={zoomed ? nav.clearZoom : retry}
    />
  );
}

function SelectionSlot({
  selection,
  failed,
  retry,
  zoom,
  eventsHref,
}: Readonly<{
  selection: StaffAnalyticsSelection | undefined;
  failed: boolean;
  retry: () => void;
  zoom: Zoom;
  eventsHref: string;
}>) {
  if (failed) {
    return (
      <AsyncState
        kind="error"
        title="The selection could not be loaded"
        message="Nothing was changed. Try again in a moment."
        onRetry={retry}
      />
    );
  }
  if (selection === undefined) return <Loading title="Loading the selection" />;
  return <SelectionPanel selection={selection} zoom={zoom} eventsHref={eventsHref} />;
}

function VisitsSlot({
  visits,
  tz,
  eventsHref,
}: Readonly<{
  visits: ReturnType<typeof useExploreVisits>;
  tz: string;
  eventsHref: (visitId: string) => string;
}>) {
  if (visits.isError) {
    return (
      <AsyncState
        kind="error"
        title="The visits could not be loaded"
        message="Nothing was changed. Try again in a moment."
        onRetry={() => void visits.refetch()}
      />
    );
  }
  if (visits.data === undefined) return <Loading title="Loading the visits" />;
  return (
    <VisitsTable
      visits={visits.data.pages.flatMap((page) => page.rows)}
      tz={tz}
      eventsHref={eventsHref}
      hasMore={visits.hasNextPage}
      loadingMore={visits.isFetchingNextPage}
      onMore={() => void visits.fetchNextPage()}
    />
  );
}

export function ExploreTab({ customerId, range, view, tz, overviewHref, eventsHref }: TabProps) {
  const nav = useExploreAddress();
  useEscapeToGoUp(nav.goUp);
  const { main, whole, selection, visits } = useExploreReads(view, customerId, range, nav);
  const name = view.opening.data?.access.displayName ?? "The customer";

  if ([main, whole, selection, visits].some((query) => isRefusal(query.error))) {
    return <RefusedState />;
  }
  if (main.isError || whole.isError) {
    return (
      <ExploreFailure
        nav={nav}
        retry={() => {
          if (main.isError) void main.refetch();
          if (whole.isError) void whole.refetch();
        }}
      />
    );
  }
  if (main.data === undefined || whole.data === undefined) {
    return <Loading title="Loading the numbers" />;
  }
  const loaded: Loaded = { metric: main.data, whole: whole.data };
  const bound: Zoom = { from: main.data.access.window.from, to: main.data.access.window.to };
  return (
    <div className="grid gap-4">
      <ExploreToolbar
        overviewHref={overviewHref}
        metric={nav.metric}
        split={nav.split}
        interval={main.data.interval}
        compare={nav.compare}
        zoom={nav.zoom}
        bound={bound}
        whole={{ from: whole.data.access.window.from, to: whole.data.access.window.to }}
        tz={tz}
        optedOut={view.opening.data?.access.consent.optedOut === true}
        onMetric={(next) => nav.setControls({ metric: next })}
        onSplit={(next) => nav.setControls({ split: next })}
        onInterval={(next) => nav.setControls({ interval: next })}
        onCompare={(next) => nav.setControls({ compare: next })}
        onClear={nav.clearZoom}
        onDates={(zoom) => nav.zoomTo(zoom)}
      />
      <AppDataNotice metric={main.data} name={name} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <ChartCard loaded={loaded} nav={nav} tz={tz} />
        <SelectionSlot
          selection={selection.data}
          failed={selection.isError}
          retry={() => void selection.refetch()}
          zoom={bound}
          eventsHref={eventsHref(nav.zoom === null ? {} : { from: nav.zoom.from, to: nav.zoom.to })}
        />
      </div>
      <VisitsSlot
        visits={visits}
        tz={tz}
        eventsHref={(visitId) => eventsHref({ visit: visitId })}
      />
    </div>
  );
}
